import { forwardRef, ReactNode, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { isEqual } from "lodash";
import { DateTime } from "luxon";

import { EmailContact } from "@/Common/Api/Contacts/Dto/EmailContact";
import { PhoneNumber } from "@/Common/Api/Contacts/Dto/PhoneNumber";
import { FeatureFlagControl } from "@/Common/Components/FeatureFlagControl";
import { AccountDetails, IAccountDetails } from "@/Common/Components/Portal/InvitationPopup/AccountDetails/AccountDetails";
import { SaveIndividualDemographicsRequest } from "@/Common/Components/Portal/InvitationPopup/Api/Dto/SaveIndividualDemographicsRequest";
import { SendInvitationRequest } from "@/Common/Components/Portal/InvitationPopup/Api/Dto/SendInvitationRequest";
import { addIndividualContact, refuseInvitation, saveIndividualDemographics, sendInvitation, sendMessage, syncMessages } from "@/Common/Components/Portal/InvitationPopup/Api/InvitationApiClient";
import { IInvitationContactEmail, IInvitationContactPhone } from "@/Common/Components/Portal/InvitationPopup/InvitationForm/ContactForm/AddContactForm/AddContactForm";
import { ContactForm } from "@/Common/Components/Portal/InvitationPopup/InvitationForm/ContactForm/ContactForm";
import { IMandatoryFieldsFormValues, MandatoryFieldsForm } from "@/Common/Components/Portal/InvitationPopup/InvitationForm/MandatoryFieldsFrom/MandatoryFieldsForm";
import { MessageForm } from "@/Common/Components/Portal/InvitationPopup/InvitationForm/MessageForm/MessageForm";
import { useInvitationPopup } from "@/Common/Components/Portal/InvitationPopup/InvitationPopupProvider";
import { usePortalCommunicationMessageForm } from "@/Common/Components/SharedItem/Hooks/usePortalCommunicationMessageForm";
import { PortalCommunicationMessageForm } from "@/Common/Components/SharedItem/PortalCommunicationMessageForm";
import { useMount } from "@/Common/CustomHooks/useMount";
import { usePrevious } from "@/Common/CustomHooks/usePrevious";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { LuxonDateHelper } from "@/Common/Helpers/LuxonDateHelper";
import { ValidationHelper } from "@/Common/Helpers/ValidationHelper";
import { ContactPointType } from "@/Common/Models/Contacts/Enums/ContactPointType";
import { PhoneType } from "@/Common/Models/Contacts/Enums/PhoneType";
import { PortalLinkedItemType } from "@/Common/Models/Portal/Enums/PortalLinkedItemType";
import { PortalRegistrationState } from "@/Common/Models/Portal/Enums/PortalRegistrationState";
import { SharedItemActionType } from "@/Common/Models/Portal/SharedItem/SharedItemActionType";
import { SharedItemMessage } from "@/Common/Models/Portal/SharedItem/SharedItemMessage";
import { SharedItemPriority } from "@/Common/Models/Portal/SharedItem/SharedItemPriority";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { sessionStore } from "@/Globals/Stores/SessionStore";

import styles from "./InvitationForm.module.scss";

interface InvitationFormHandle extends InvitationFormValid {
    send: (contactFacetId: string, contactId: string) => Promise<void>;
    refuse: (id: string) => Promise<void>;
    invite: (id: string) => Promise<void>;
}

interface InvitationFormChangeEvent {
    values: InvitationFormValues;
    validator: InvitationFormValid;
}

interface InvitationFormValid {
    isValidToSend: boolean;
    isValidToInvite: boolean;
}

interface IMessage {
    subject: string | null;
    body: string | null;
    priority: SharedItemPriority | null;
    canPatientReplyWithMessage: boolean | null;
}

interface InvitationFormValues {
    email?: IInvitationContactEmail;
    phone?: IInvitationContactPhone;
    fields?: IMandatoryFieldsFormValues;
    message?: IMessage;
}

interface InvitationFormProps {
    hidden?: boolean;
    fields: IMandatoryFieldsFormValues;
    emails: IInvitationContactEmail[];
    phones: IInvitationContactPhone[];
    details?: IAccountDetails;
    onChange: (e: InvitationFormChangeEvent) => void;
}

const InvitationForm = forwardRef<InvitationFormHandle, InvitationFormProps>((props, ref) => {
    const { getText } = useTranslation();

    const isBidirectionalCommunicationEnabled = cacheStore.ClinicSettings.EnableBidirectionalCommunication;
    const [values, setValues] = useState<InvitationFormValues>({
        fields: { ...props.fields },
        email: props.emails[0]
    });
    const previousValues = usePrevious(values);
    const {
        redirect,
        hasAccount,
        underAgeOfConsent,
        representatives,
        anyRepresentative,
        unlocking,
        resending,
        setResending,
        createOwn,
        accountStatus,
        setCreateOwn
    } = useInvitationPopup();

    const {
        formState,
        isFormValid,
        onSetFormState
    } = usePortalCommunicationMessageForm();

    const isNewContact = useRef(false);

    const accountInvitationLocked = useMemo<boolean>(
        () => accountStatus === (PortalRegistrationState.InvitationLocked) || (props.details.status === PortalRegistrationState.InvitationLocked),
        [accountStatus, props.details.status]
    );

    const isContactValid = useMemo<boolean>(
        () => {
            const { email, phone } = values;

            if (!!email) {
                return !!email.contactPointId && !!email.address && EmailContact.validateEmail(email.address);
            }

            if (!!phone) {
                const validationHelper = new ValidationHelper();

                return !!phone.contactPointId
                    && !!phone.countryLookupId
                    && !!phone.phoneTypeId
                    && !!phone.number
                    && ValidationHelper.isExactMatch(
                        phone.number,
                        new RegExp(validationHelper.getPhoneNumberValidationRule(phone.countryLookupId, null)?.ValidationRegexPattern ?? "", "i")
                    );
            }

            return false;
        },
        [values]
    );

    const isValidToSend = useMemo<boolean>(
        () => {
            if ((hasAccount && !resending) || (!hasAccount && !!representatives.length)) {
                if (cacheStore.ClinicSettings.EnableBidirectionalCommunication) {
                    return isFormValid;
                }
                const { message } = values;

                return !!message?.subject && !!message?.body;
            }

            return false;
        },
        [hasAccount, resending, representatives.length, values, isFormValid]
    );

    const previousIsValidToSend = usePrevious(isValidToSend);

    const messageFormSubHeading = useMemo<ReactNode>(
        () => {
            let heading: ReactNode;

            if (hasAccount && accountStatus === PortalRegistrationState.RegistrationPending && !resending) {
                heading = (
                    <p className={styles.messageFormSubHeading + " " + styles.errorHeading}>
                        {getText("PortalInvitation_InactivePortalAccountMessageLabel")}
                    </p>
                );
            }
            else if (accountStatus !== PortalRegistrationState.RegistrationComplete) {
                heading = (
                    <p className={styles.messageFormSubHeading}>
                        {getText("PortalInvitation_MessageAvailabilityLabel")}
                    </p>
                );
            }

            if (!hasAccount && !!representatives.length) {
                heading = (
                    <p className={styles.messageFormSubHeading + " " + styles.errorHeading}>
                        {getText("PortalInvitation_PatientRepresentativeActivePortalAccountMessageLabel")}
                    </p>
                );
            }

            return heading;
        },
        [accountStatus, hasAccount, resending, representatives.length, getText]
    );

    const isValidToInvite = useMemo<boolean>(
        () => {
            if (!hasAccount || resending || unlocking) {
                return isContactValid && Object.values(values.fields).every(Boolean);
            }

            return false;
        },
        [hasAccount, isContactValid, resending, unlocking, values.fields]
    );

    const previousIsValidToInvite = usePrevious(isValidToInvite);

    const isMandatoryFieldsMissing = useMemo<boolean>(
        () => Object.values(props.fields).some(field => !field),
        [props.fields]
    );

    const representativeHasPermissions = representatives.some(representative => !!representative.permissions.length);

    const handleSend = useCallback(
        async (contactFacetId: string, contactId: string): Promise<void> => {
            let objectId = null;
            let sharedItemType = null;
            let canPatientReplyWithMessage = false;
            let subject = null;
            const portalMessages: SharedItemMessage[] = [];

            if (cacheStore.ClinicSettings.EnableBidirectionalCommunication) {
                sharedItemType = PortalLinkedItemType.None;
                subject = formState.subject;
                canPatientReplyWithMessage = formState.canPatientReplyWithMessage;
                portalMessages.push(
                    SharedItemMessage.createNewSharedItemMessage(formState.messageText, formState.priority)
                );
            }
            else {
                const response = await sendMessage({
                    patientId: contactFacetId,
                    employeeId: cacheStore.GetCurrentEmployee().ContactFacet_id,
                    followUpLookupId: cacheStore.FindLookupByLookupCode("RFLeftMessage").LookupID,
                    version: 1,
                    note: values.message.body
                });

                if (response.errors != null || response.errors.length > 0)
                    return;

                subject = values.message.subject;
                objectId = response.data.NewCommunicationId;
                sharedItemType = PortalLinkedItemType.CommunicationHistory;
            }

            await syncMessages({
                ObjectId: objectId,
                SharedItemType: sharedItemType,
                EmployeeContactId: cacheStore.GetCurrentEmployee().Contact_id,
                PatientContactId: contactId,
                CreatedByUserId: cacheStore.GetCurrentEmployee().User_id,
                IncludeLinkedItem: false,
                Subject: subject,
                Messages: portalMessages,
                CanPatientReplyWithMessage: canPatientReplyWithMessage
            });
        },
        [formState, values]
    );

    const handleRefuse = async (contactId: string): Promise<void> => {
        await refuseInvitation(contactId);

        sessionStore.dispatch(
            LocalOperations.Portal.Portal_UpdateState,
            {
                patientContactId: contactId,
                portalStatus: PortalRegistrationState.PortalRefused
            }
        );
    };

    const handleInvite = useCallback(
        async (contactId: string): Promise<void> => {
            const { email, phone } = values;
            const contactPointId = email?.contactPointId ?? phone?.contactPointId;
            let message = values.message;

            if (cacheStore.ClinicSettings.EnableBidirectionalCommunication) {
                message = {
                    subject: formState.subject,
                    body: formState.messageText,
                    priority: formState.priority,
                    canPatientReplyWithMessage: formState.canPatientReplyWithMessage
                };
            }

            const data: SendInvitationRequest = {
                contactId,
                invitationContact: {
                    contactPointTypeLookupId: contactPointId,
                    emailAddress: email ? email.address : null,
                    phoneNumber: phone ? {
                        phoneTypeLookupId: phone.phoneTypeId,
                        countryLookupId: phone.countryLookupId,
                        number: phone.number
                    } : null
                },
                mandatoryFieldsData: !isEqual(values.fields, props.fields) ? values.fields : null,
                invitationMessage: message
            };

            const response = await sendInvitation(data);

            if (response.isSuccess) {
                sessionStore.dispatchAsync(
                    LocalOperations.Portal.Portal_UpdateState,
                    {
                        patientContactId: contactId,
                        portalStatus: PortalRegistrationState.RegistrationPending
                    }
                );

                if (isNewContact.current) {
                    if (email) {
                        sessionStore.dispatch(LocalPatientOperations.Profile.Contacts_AddEmail, {
                            value: email
                        });
                    }

                    if (phone) {
                        sessionStore.dispatch(LocalPatientOperations.Profile.Contacts_AddPhone, {
                            value: phone
                        });
                    }

                    sessionStore.dispatch(LocalPatientOperations.Profile.Contacts_SaveClick);
                }

                if ("fields" in values) {
                    sessionStore.dispatchAsync(
                        LocalPatientOperations.Profile.Demographics_UpdateMandatoryFields,
                        {
                            contactId,
                            fields: values.fields
                        }
                    );
                }
            }
            else {
                sessionStore.showPopup(response.errors);
            }
        },
        [formState, props.fields, values]
    );

    const handleSaveIndividualDemographics = useCallback(
        async (): Promise<void> => {
            const individual = patientStore.getCurrentPatient()?.IndividualResponse;
            const newValues = values.fields;

            const data: SaveIndividualDemographicsRequest = {
                ContactId: individual.Contact_id,
                FirstName: newValues.firstName ?? individual.FirstName,
                LastName: newValues.lastName ?? individual.LastName,
                MiddleName: individual.MiddleName,
                SecondName: individual.SecondName,
                PrefixLookupId: individual.PrefixLookupId,
                SuffixLookupId: individual.SuffixLookupId,
                MothersFirstName: individual.MotherFirstName,
                MothersLastName: individual.MotherLastName,
                FathersFirstName: individual.FatherFirstName,
                FathersLastName: individual.FatherLastName,
                MaritalStatusLookupId: individual.MaritalStatusLookupId,
                PreferredOfficialLanguageLookupId: individual.PreferredOfficialLanguageLookupId,
                PreferredSpokenLanguageLookupId: individual.PreferredSpokenLanguageLookupId,
                GenderLookupId: newValues.sexAtBirthLookupId ?? individual.GenderLookupId,
                DateOfBirth: newValues.dateOfBirth?.toDateOnly() ?? individual.DateOfBirth?.toDateOnly,
                ReligionCode: individual.Religion,
                PreferredName: individual.PreferredName,
                EthnicityKeys: individual.EthnicityKeys,
                GenderIdentity: individual.GenderIdentity,
                PreferredPronouns: individual.PreferredPronouns,
                SpecifiedPreferredPronouns: individual.SpecifiedPreferredPronouns
            };
            const response = await saveIndividualDemographics(data);

            if (response.isSuccess) {
                if ("fields" in values) {
                    sessionStore.dispatchAsync(
                        LocalPatientOperations.Profile.Demographics_UpdateMandatoryFields,
                        {
                            contactId: individual.Contact_id,
                            fields: values.fields
                        }
                    );
                }
            }
            else {
                sessionStore.showPopup(response.errors);
            }
        },
        [values]
    );

    const handleAddIndividualContact = useCallback(
        async (): Promise<void> => {
            const individual = patientStore.getCurrentPatient()?.IndividualResponse;
            const { email, phone } = values;

            const emailToSave = EmailContact.newEmail();
            emailToSave.Address = email?.address;
            emailToSave.ContactPointTypeLookupId = email?.contactPointId;
            emailToSave.IsOkToContact = true;
            emailToSave.NotificationsEnabled = true;

            const phoneToSave = PhoneNumber.newPhone(ContactPointType.Personal, PhoneType.Mobile);
            phoneToSave.Number = phone?.number;
            phoneToSave.CountryLookupId = phone?.countryLookupId;
            phoneToSave.ContactPointTypeLookupId = phone?.contactPointId;
            phoneToSave.PhoneTypeLookupId = phone?.phoneTypeId;
            phoneToSave.NotificationsEnabled = true;

            const data = {
                ContactId: individual.Contact_id,
                Email: emailToSave,
                Phone: phoneToSave
            };
            const response = await addIndividualContact(data);

            if (response.isSuccess) {
                if (email) {
                    sessionStore.dispatch(LocalPatientOperations.Profile.Contacts_AddEmail, {
                        value: email
                    });
                }

                if (phone) {
                    sessionStore.dispatch(LocalPatientOperations.Profile.Contacts_AddPhone, {
                        value: phone
                    });
                }

                sessionStore.dispatch(LocalPatientOperations.Profile.Contacts_SaveClick);
            }
            else {
                sessionStore.showPopup(response.errors);
            }
        },
        [values]
    );

    useImperativeHandle(
        ref,
        () => ({
            send: handleSend,
            refuse: handleRefuse,
            invite: handleInvite,
            isValidToSend,
            isValidToInvite
        }),
        [handleInvite, handleSend, isValidToInvite, isValidToSend]
    );

    /**
    * Trigger onChange event
    * Effect triggers onChange event every time when form values or validation has been updated
    */
    useEffect(
        () => {
            if (!isEqual(previousValues, values) || !isEqual(isValidToInvite, previousIsValidToInvite) || !isEqual(isValidToSend, previousIsValidToSend)) {
                props.onChange({ values, validator: { isValidToInvite, isValidToSend } });
            }
        },
        [isValidToInvite, isValidToSend, props, previousIsValidToInvite, previousIsValidToSend, previousValues, values]
    );

    useMount(() => {
        props.onChange?.({ values, validator: { isValidToInvite, isValidToSend } });
    });

    const refusalDate = (): string => LuxonDateHelper.formatToFullDateTime(props.details.refusalDate);

    const getAgeWarning = (date: Date): string => {
        if (date === null || date === undefined)
            return "";

        const now = DateTime.now();
        const dob = DateTime.fromJSDate(date);

        const diff = now.diff(dob, ["years", "months", "days", "hours"]);

        if (diff.days < 0)
            return "";
        else if (diff.years < 1 && diff.months < 1)
            return getText("PortalInvitation_PatientAgeInDaysMessageLabel", { age: diff.days });
        else if (diff.years < 1)
            return getText("PortalInvitation_PatientAgeInMonthsAndDaysMessageLabel", { months: diff.months, days: diff.days });
        else if (diff.years < 2)
            return getText("PortalInvitation_PatientAgeInYearsMonthsAndDaysMessageLabel", { years: diff.years, months: diff.months, days: diff.days });

        return getText("PortalInvitation_PatientAgeInYearsAndMonthsMessageLabel", { years: diff.years, months: diff.months });
    };

    const updateMessage = (updates: Partial<IMessage>): void => {
        setValues(currentValues => ({
            ...currentValues,
            message: { ...currentValues.message, ...updates }
        }));
    };

    return (
        <form name="invitation-form-provider" className={props.hidden ? styles.hidden : ""}>
            {props.details.status === PortalRegistrationState.PortalRefused &&
                (<p className="margin-md-bottom red-text"><strong>{getText("PortalInvitation_RefusedNoteLabel", { date: refusalDate() })}</strong></p>)
            }

            {underAgeOfConsent.check && (
                <p className={styles.underAgeOfConsent}>
                    {getAgeWarning(underAgeOfConsent.dateOfBirth)}
                </p>
            )}

            {(isMandatoryFieldsMissing && (!hasAccount || (hasAccount && resending) || (hasAccount && unlocking))) && (
                <MandatoryFieldsForm
                    values={values.fields}
                    onChange={(fields: IMandatoryFieldsFormValues) => {
                        setValues(currentValues => ({
                            ...currentValues, fields
                        }));
                    }}
                />
            )}

            {(hasAccount && !accountInvitationLocked && !resending) && (
                <AccountDetails
                    {...props.details}
                    isPopup
                    onResendInvitation={() => setResending(true)}
                />
            )}

            {(!!representatives.length && representativeHasPermissions && !createOwn) && (
                <p className={styles.formLink}>
                    <a
                        onClick={e => {
                            e.preventDefault();
                            setCreateOwn(true);
                        }}
                    >
                        {getText("PortalInvitation_SendInvitationForOwnAccountLabel")}
                    </a>
                </p>
            )}

            {((!hasAccount && !representativeHasPermissions) || accountInvitationLocked || (hasAccount && resending) || (!hasAccount && createOwn)) && (
                <ContactForm
                    email={values.email}
                    phone={values.phone}
                    emails={props.emails}
                    phones={props.phones}
                    onAddContact={state => isNewContact.current = state}
                    onPhoneChange={phone => setValues(currentValues => ({
                        ...currentValues, phone
                    }))}
                    onEmailChange={email => setValues(currentValues => ({
                        ...currentValues, email
                    }))}
                />
            )}

            {(anyRepresentative || underAgeOfConsent.check) && !representativeHasPermissions && (
                <p className={styles.formLink}>
                    <a
                        onClick={e => {
                            e.preventDefault();
                            redirect();

                            if ("fields" in values) {
                                handleSaveIndividualDemographics();
                            }

                            if (isNewContact.current) {
                                handleAddIndividualContact();
                            }
                        }}
                    >
                        {getText("PortalInvitation_SendInvitationToRepresentativeLabel")}
                    </a>
                </p>
            )}

            {!!representatives.length && representativeHasPermissions && (
                <>
                    <h4 className="bottom-border">
                        {getText("AccessRightsLabel")}
                    </h4>

                    <div className={styles.formLink}>
                        <a
                            onClick={e => {
                                e.preventDefault();
                                redirect();
                            }}
                        >
                            {getText("PortalInvitation_SeeRelationsLabel")}
                        </a>
                    </div>
                </>
            )}

            {!(accountInvitationLocked && !unlocking) && (
                <FeatureFlagControl
                    flag={isBidirectionalCommunicationEnabled}
                    activeFeature={(
                        <>
                            <div className={styles.messageHeading}>
                                {getText("PortalInvitation_SendMessageLabel")} {accountStatus === PortalRegistrationState.RegistrationComplete ? null : `(${getText("OptionalLabel")})`}
                            </div>
                            {messageFormSubHeading}
                            <PortalCommunicationMessageForm
                                componentId="SendToPortal_InvitationMessageForm"
                                actionType={SharedItemActionType.New}
                                containerClass="shared-item-new-message"
                                formContainerClass="portal-communication-form-new-message"
                                isPopup
                                hideToField
                                onNewPortalCommunicationMessageChange={newState => onSetFormState({
                                    ...formState,
                                    ...newState
                                })}
                            />
                        </>
                    )
                    }
                    fallbackFeature={(
                        <MessageForm
                            required={accountStatus === PortalRegistrationState.RegistrationComplete}
                            subHeading={messageFormSubHeading}
                            values={values.message}
                            onChange={(message: IMessage) => updateMessage(message)}
                        />
                    )}
                />
            )}

        </form>
    );
});

InvitationForm.displayName = "InvitationForm";

export type { IMessage, InvitationFormChangeEvent, InvitationFormHandle, InvitationFormValid, InvitationFormValues };

export { InvitationForm };
